import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, ShieldAlert, Stethoscope, Wrench } from "lucide-react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";
import { TypeUser } from "@/types/userType";
import { getInconsistentSales } from "@/Api/saleNormalization";
import { NormalizeSaleModal } from "./components/NormalizeSaleModal";
import { InconsistentSaleRow } from "./types";

/**
 * Normalização de vendas inconsistentes.
 *
 * Lista as vendas que ficaram travadas por terem recebido aprovação e recusa
 * quase ao mesmo tempo (ou por etapas duplicadas) e permite corrigi-las
 * escolhendo qual lado prevalece. A verificação de acesso abaixo é apenas de
 * interface: o backend bloqueia os endpoints pelo middleware
 * "panel.permission" e limita o escopo ao tenant do usuário autenticado.
 */
export const PageList = () => {
  const { user } = useAuth();
  const { user: currentUser, hasPermission } = usePermissions();

  const isSuperAdmin = user?.user_type_id === TypeUser.admin;
  const isTenantUser = user?.user_type_id === TypeUser.client;

  const isTenantAdmin =
    isTenantUser && hasPermission(PERMISSIONS.MANAGE_SALE_NORMALIZATION);

  const isCheckingPermissions = !isSuperAdmin && isTenantUser && !currentUser;
  const canAccess = isSuperAdmin || isTenantAdmin;

  const [rows, setRows] = useState<InconsistentSaleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saleIdFilter, setSaleIdFilter] = useState("");
  const [selected, setSelected] = useState<InconsistentSaleRow | null>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);

    const response = await getInconsistentSales(1, {
      sale_id: saleIdFilter || undefined,
      per_page: 50,
    });

    setLoading(false);

    if (!response.ok) {
      toast.error(response.data?.message ?? "Não foi possível carregar as vendas.");
      return;
    }

    const data = response.data?.response?.data ?? [];

    setRows(
      data.map((sale: Record<string, any>) => ({
        id: sale.id,
        uuid: sale.uuid,
        total: Number(sale.total ?? 0),
        sale_status_id: sale.sale_status_id,
        sale_status: sale.sale_status?.name ?? "—",
        unity: sale.unity?.name ?? "—",
        client: sale.client?.name ?? "—",
        seller: sale.seller?.user?.name ?? "—",
        created_at: sale.created_at,
        steps: sale.process_step_sales ?? [],
        issues: sale.normalization_issues ?? [],
      }))
    );
  }, [saleIdFilter]);

  useEffect(() => {
    if (!canAccess) return;

    loadRows();
  }, [canAccess, loadRows]);

  if (isCheckingPermissions) {
    return (
      <Main>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Main>
    );
  }

  if (!canAccess) {
    return (
      <Main>
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <Lock className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Acesso restrito</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            A normalização de vendas está disponível para o Super Admin e para
            administradores com a permissão correspondente.
          </p>
        </div>
      </Main>
    );
  }

  return (
    <Main>
      <PageHeader
        icon={Stethoscope}
        title="Normalização de Vendas"
        description="Vendas travadas por etapas inconsistentes — tipicamente pré-vendas aprovadas e recusadas ao mesmo tempo"
      />

      <div className="mb-4 flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Uso restrito. Toda correção passa por pré-visualização obrigatória, exige um
          motivo e fica registrada com o retrato das linhas antes e depois.
          {!isSuperAdmin && " Você enxerga apenas as vendas da sua empresa."}
        </span>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Label htmlFor="sale-id-filter" className="mb-1 block">
            Nº da venda
          </Label>
          <Input
            id="sale-id-filter"
            value={saleIdFilter}
            onChange={(event) => setSaleIdFilter(event.target.value.replace(/\D/g, ""))}
            placeholder="210111"
          />
        </div>
        <Button variant="outline" onClick={loadRows} disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Buscar
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Venda</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Vendedor</TableHead>
              <TableHead>Unidade</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Inconsistências</TableHead>
              <TableHead className="w-40" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                  Nenhuma venda inconsistente encontrada.
                </TableCell>
              </TableRow>
            )}

            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">#{row.id}</TableCell>
                <TableCell>{row.client}</TableCell>
                <TableCell>{row.seller}</TableCell>
                <TableCell>{row.unity}</TableCell>
                <TableCell className="text-right">
                  {row.total.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </TableCell>
                <TableCell>{row.sale_status}</TableCell>
                <TableCell>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {row.issues.map((issue) => (
                      <li key={issue.code}>• {issue.label}</li>
                    ))}
                  </ul>
                </TableCell>
                <TableCell>
                  <Button size="sm" onClick={() => setSelected(row)}>
                    <Wrench className="mr-2 h-3.5 w-3.5" />
                    Normalizar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <NormalizeSaleModal
        sale={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onApplied={loadRows}
      />
    </Main>
  );
};

export default PageList;
